import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { Badge, type BadgeTone } from "../primitives/badge";

/**
 * ActionRow. Compact horizontal row used in scannable lists - the
 * /attention "today's actions" page is the canonical caller. Each row
 * carries:
 *
 *   - leading glyph (Phosphor icon or any ReactNode)
 *   - kind chip (microstep / reset / recipe / story / article / habit)
 *   - title + optional sub line
 *   - optional trailing meta (duration, source)
 *   - primary action (button) at the end
 *
 * Distinct from PlanActionCard - that's the full editorial card with
 * a poster + when/why. ActionRow is the dense scannable row.
 */
export type ActionRowKind = "microstep" | "reset" | "recipe" | "article" | "story" | "habit";

const KIND_TO_BADGE: Record<ActionRowKind, BadgeTone> = {
  microstep: "story",
  reset: "reset",
  recipe: "recipe",
  article: "article",
  story: "story",
  habit: "habit",
};

const KIND_LABEL: Record<ActionRowKind, string> = {
  microstep: "Microstep",
  reset: "Reset",
  recipe: "Recipe",
  article: "Article",
  story: "Story",
  habit: "Habit",
};

export interface ActionRowProps {
  kind: ActionRowKind;
  /** Leading glyph - a Phosphor icon node or other element. */
  glyph?: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  /** Right-side meta (e.g. "5 min" or "Open"). */
  meta?: ReactNode;
  /** Primary action node (typically a <Button>). */
  action?: ReactNode;
  /** When true, the row reads as completed: muted text, leaf-soft fill. */
  done?: boolean;
  className?: string;
}

export function ActionRow({
  kind,
  glyph,
  title,
  sub,
  meta,
  action,
  done = false,
  className,
}: ActionRowProps) {
  return (
    <article
      className={cn(
        "grid items-center gap-3 rounded-2xl border p-4 sm:grid-cols-[40px_minmax(0,1fr)_auto]",
        done
          ? "border-leaf/30 bg-surface-accent-leaf/50"
          : "border-border-card bg-surface-elevated hover:shadow-card",
        className,
      )}
    >
      {glyph ? (
        <span
          aria-hidden="true"
          className={cn(
            "grid h-10 w-10 shrink-0 place-items-center rounded-xl",
            done
              ? "bg-surface-accent-leaf text-ink-accent-leaf"
              : "bg-surface-secondary text-ink-brand",
          )}
        >
          {glyph}
        </span>
      ) : (
        <span aria-hidden="true" />
      )}
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <Badge tone={KIND_TO_BADGE[kind]}>{KIND_LABEL[kind]}</Badge>
          {meta ? (
            <span className="font-sans font-medium text-xs text-ink-tertiary">{meta}</span>
          ) : null}
        </div>
        <h3
          className={cn(
            "m-0 font-sans text-lg font-medium leading-snug tracking-tight",
            done ? "text-ink-secondary line-through decoration-leaf-dark/40" : "text-ink-primary",
          )}
        >
          {title}
        </h3>
        {sub ? <p className="m-0 text-sm leading-relaxed text-ink-secondary">{sub}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </article>
  );
}
